import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';

import {
    SERVICE_BILLING_START_MS,
    isServiceBillable
} from '../../core/whatsapp/service-billing.generated';

/**
 * The "why am I paying for these messages?" explainer.
 *
 * One component, three entry points: the announcement banner's "Learn more",
 * the link under the conversation composer, and the pricing page's calculator.
 * Keeping the copy in one place is the point — three hand-maintained versions of
 * a pricing explanation is how customers get told three different things.
 *
 * Uses SweetAlert to match the house pattern for informational dialogs (see
 * `showMessagingLimitsInfo` in organization-settings).
 */
@Injectable({ providedIn: 'root' })
export class ServiceBillingInfoService {

    /** The date Meta starts charging for free-form replies. */
    readonly startsAt = new Date(SERVICE_BILLING_START_MS);

    /** Whether that has happened yet. */
    get isBillable(): boolean {
        return isServiceBillable();
    }

    /**
     * Explain the change.
     *
     * The copy is deliberately date-aware: before the cutover it reads as a
     * heads-up, after it reads as an explanation of a charge already on the bill.
     * Both are true at the time they are shown, and neither needs a deploy.
     *
     * @param rateText Optional per-message rate, already formatted, e.g. "$0.0095".
     */
    show(rateText?: string): void {
        const when = this.startsAt.toLocaleDateString('en-GB', {
            day: 'numeric', month: 'long', year: 'numeric'
        });

        const rateLine = rateText ?
            `<p class="mb-2">For your market that is <strong>${rateText} per reply</strong>.</p>` :
            '';

        const intro = this.isBillable ?
            `<p class="mb-2">Since <strong>${when}</strong>, Meta charges for every message your
             business sends inside the 24-hour reply window — including replies typed by your
             team and answers sent automatically.</p>` :
            `<p class="mb-2">From <strong>${when}</strong>, Meta will charge for every message your
             business sends inside the 24-hour reply window — including replies typed by your
             team and answers sent automatically. Until then they remain free.</p>`;

        Swal.fire({
            title: 'Why am I paying for these messages?',
            icon: 'info',
            width: '640px',
            confirmButtonText: 'Got it',
            confirmButtonColor: '#556ee6',
            html: `
                <div class="text-start">
                    ${intro}
                    ${rateLine}

                    <h6 class="mt-3 mb-2">What has actually changed</h6>
                    <p class="mb-2">The 24-hour window itself has not gone anywhere. When someone
                    messages you first, you can still reply in your own words for 24 hours, with no
                    template and no approval wait. That freedom is unchanged — what changed is that
                    Meta now bills for it.</p>

                    <h6 class="mt-3 mb-2">What is still free</h6>
                    <ul class="mb-2 ps-3">
                        <li>Everything customers send <em>to</em> you.</li>
                        <li>Conversations started from a click-to-WhatsApp ad, for 72 hours.</li>
                        <li>Replies on your website chat widget — those never touch WhatsApp.</li>
                    </ul>

                    <h6 class="mt-3 mb-2">What this is not</h6>
                    <p class="mb-2">This is not an Echo charge. Meta bills your WhatsApp Business
                    Account directly, and Echo adds no markup. We show the cost so you can see it
                    coming, cap it, and plan around it.</p>

                    <h6 class="mt-3 mb-2">Keeping the cost down</h6>
                    <ul class="mb-0 ps-3">
                        <li>Answer in fewer, fuller messages rather than several short ones —
                            each message is billed separately.</li>
                        <li>Set a monthly support-message limit on the Messaging Pricing page so
                            you are told before a busy month becomes an expensive one.</li>
                    </ul>
                </div>
            `
        });
    }
}
